import { Repeat, CalendarClock, Waves, Server, Move, ServerCog, Radio, Waypoints, Headphones } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const SOLUTIONS = [
  { icon: Repeat, title: "Fiber on Lease", desc: "Dedicated fiber connectivity on lease, structured to your route and capacity needs." },
  { icon: CalendarClock, title: "Fiber on Rental Basis", desc: "Flexible rental-based fiber access for short and long-term requirements." },
  { icon: Waves, title: "Dark Fiber", desc: "Unlit fiber strands for organizations that manage their own transmission equipment." },
  { icon: Server, title: "Co-location", desc: "Space and infrastructure support for hosting telecom and networking equipment." },
  { icon: Move, title: "Fiber Shifting", desc: "Careful shifting and relocation of live and planned fiber infrastructure." },
  { icon: ServerCog, title: "Network Infrastructure", desc: "Switches, routers, SFPs and the supporting hardware that keeps traffic moving." },
  { icon: Radio, title: "RF Links", desc: "Point-to-point wireless connectivity where fiber is impractical or delayed." },
  { icon: Waypoints, title: "Point-to-Point Connectivity", desc: "Dedicated links between sites, buildings, towers and data centers." },
  { icon: Headphones, title: "Telecom Infrastructure Support", desc: "24/7, 365-day support for critical telecom and fiber infrastructure." },
];

export default function Connectivity() {
  return (
    <section
      id="connectivity"
      data-testid="connectivity-section"
      className="relative overflow-hidden bg-navy-900 py-24 lg:py-32"
    >
      <div className="jtk-dotgrid pointer-events-none absolute inset-0 opacity-50" />
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.12]"
        viewBox="0 0 1200 600"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <path d="M0 480 C 220 380, 340 520, 560 420 S 900 260, 1200 340" stroke="#06B6D4" strokeWidth="1.5" fill="none" />
        <path d="M0 220 C 260 300, 420 140, 640 240 S 940 420, 1200 300" stroke="#2563EB" strokeWidth="1.5" fill="none" />
        <path d="M120 600 C 300 420, 520 480, 760 320 S 1000 160, 1200 200" stroke="#3B82F6" strokeWidth="1" fill="none" />
        <circle cx="560" cy="420" r="4" fill="#06B6D4" />
        <circle cx="640" cy="240" r="4" fill="#2563EB" />
        <circle cx="760" cy="320" r="4" fill="#67E8F9" />
      </svg>
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[380px] w-[380px] rounded-full bg-cyan-500/10 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHead
          dark
          eyebrow="Fiber & Connectivity Solutions"
          title="Reliable Connectivity. Flexible Infrastructure."
          sub="Connectivity models that fit how your organization actually operates — leased, rented, dark or fully managed."
        />

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <div
                data-testid={`connectivity-card-${i}`}
                className="group h-full rounded-2xl border border-white/[0.08] bg-white/[0.04] p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-cyan-400/30 hover:bg-white/[0.07] hover:shadow-[0_20px_50px_rgba(6,182,212,0.12)]"
              >
                <span className="inline-flex rounded-xl border border-cyan-400/20 bg-cyan-400/10 p-3 text-cyan-300 transition-colors duration-300 group-hover:bg-cyan-400 group-hover:text-navy">
                  <s.icon size={20} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-white">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
